import { useEffect, useState } from "react";

import { useCall } from "../state/CallProvider";
import type { TransferKind } from "../telephony/types";
import {
  IconHangup, IconHold, IconKeypad, IconMic, IconMicOff, IconMore, IconNotes,
  IconPhone, IconPlus, IconRecord, IconSpeaker, IconTransfer,
} from "./icons";

function initials(name?: string, number?: string): string {
  if (name) return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
  return (number ?? "?").replace(/[^0-9]/g, "").slice(-2) || "#";
}
function mmss(s: number): string {
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
function useElapsed(fromMs?: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!fromMs) return;
    const t = window.setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [fromMs]);
  return fromMs ? Math.max(0, Math.floor((now - fromMs) / 1000)) : 0;
}

const DTMF = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

export function CallCard() {
  const { state, hangup, answer, toggleMute, toggleHold, toggleRecording, transfer, conference, sendDigit, setNotes } = useCall();
  const call = state.call!;
  const { status } = state;
  const elapsed = useElapsed(call.connectedAtMs);
  const [pad, setPad] = useState(false);
  const [pane, setPane] = useState<null | "xfer" | "conf">(null);
  const [target, setTarget] = useState("");
  const [kind, setKind] = useState<TransferKind>("external");

  const closePane = () => { setPane(null); setTarget(""); };
  const doTransfer = (mode: "blind" | "warm") => { if (target.trim()) { transfer(target.trim(), kind, mode); closePane(); } };
  const doConference = () => { if (target.trim()) { conference(target.trim()); closePane(); } };

  const statusLabel =
    status === "incoming" ? "Incoming call" :
    status === "dialing" ? "Calling…" :
    status === "ringing" ? "Ringing…" :
    status === "onHold" ? "On hold" :
    status === "ended" ? "Call ended" : mmss(elapsed);

  return (
    <div className="cc">
      <div className="cc-avatar" aria-hidden>
        {call.contact.avatarUrl ? <img src={call.contact.avatarUrl} alt="" /> : initials(call.contact.name, call.contact.number)}
      </div>
      <div className="cc-name">{call.contact.name ?? call.contact.number}</div>
      {call.contact.name && <div className="cc-num">{call.contact.number}</div>}
      {call.contact.company && <div className="cc-company">{call.contact.company}</div>}
      <div className="cc-meta"><span className="cc-timer">{statusLabel}</span></div>
      {call.contact.recordId && <div className="cc-pop">🔗 Matched CRM record</div>}

      {status === "incoming" ? (
        <div className="cc-actions-row">
          <div>
            <button className="cc-hangup cc-answer" onClick={answer} aria-label="Answer"><IconPhone /></button>
            <div className="cc-action-label">Answer</div>
          </div>
          <div>
            <button className="cc-hangup" onClick={hangup} aria-label="Decline"><IconHangup /></button>
            <div className="cc-action-label">Decline</div>
          </div>
        </div>
      ) : (
        <>
          {(status === "active" || status === "onHold") && (
            <>
              <div className="cc-grid">
                <button className={"cc-btn" + (call.muted ? " on" : "")} onClick={toggleMute}>
                  <span className="cc-ic">{call.muted ? <IconMicOff /> : <IconMic />}</span>{call.muted ? "Unmute" : "Mute"}
                </button>
                <button className={"cc-btn" + (pad ? " on" : "")} onClick={() => setPad((v) => !v)}>
                  <span className="cc-ic"><IconKeypad /></span>Keypad
                </button>
                <button className="cc-btn"><span className="cc-ic"><IconSpeaker /></span>Audio</button>
                <button className={"cc-btn" + (pane === "conf" ? " on" : "")} onClick={() => setPane(pane === "conf" ? null : "conf")}>
                  <span className="cc-ic"><IconPlus /></span>Add
                </button>
                <button className={"cc-btn" + (call.onHold ? " on" : "")} onClick={toggleHold}>
                  <span className="cc-ic"><IconHold /></span>{call.onHold ? "Resume" : "Hold"}
                </button>
                <button className={"cc-btn" + (pane === "xfer" ? " on" : "")} onClick={() => setPane(pane === "xfer" ? null : "xfer")}>
                  <span className="cc-ic"><IconTransfer /></span>Transfer
                </button>
                <button className={"cc-btn rec" + (call.recording ? " on" : "")} onClick={toggleRecording}>
                  <span className="cc-ic"><IconRecord /></span>{call.recording ? "Stop" : "Record"}
                </button>
                <button className="cc-btn"><span className="cc-ic"><IconNotes /></span>Notes</button>
                <button className="cc-btn" disabled><span className="cc-ic"><IconMore /></span>More</button>
              </div>
              {pad && (
                <div className="dp-keys" style={{ width: "100%", marginBottom: 8 }}>
                  {DTMF.map((d) => (
                    <button key={d} className="dp-key" onClick={() => sendDigit(d)}>{d}</button>
                  ))}
                </div>
              )}
              {pane === "xfer" && (
                <div className="cc-pane">
                  <div className="cc-pane-title">Transfer call</div>
                  <input className="cc-pane-input" autoFocus placeholder="Number, agent, or queue"
                    value={target} onChange={(e) => setTarget(e.target.value)} />
                  <div className="cc-seg">
                    {(["external", "agent", "queue"] as TransferKind[]).map((k) => (
                      <button key={k} className={"cc-seg-btn" + (kind === k ? " on" : "")} onClick={() => setKind(k)}>{k}</button>
                    ))}
                  </div>
                  <div className="cc-pane-actions">
                    <button className="cc-pane-btn" disabled={!target.trim()} onClick={() => doTransfer("blind")}>Blind</button>
                    <button className="cc-pane-btn primary" disabled={!target.trim()} onClick={() => doTransfer("warm")}>Warm</button>
                    <button className="cc-pane-btn ghost" onClick={closePane}>Cancel</button>
                  </div>
                </div>
              )}
              {pane === "conf" && (
                <div className="cc-pane">
                  <div className="cc-pane-title">Add to call</div>
                  <input className="cc-pane-input" autoFocus placeholder="Number or agent to add"
                    value={target} onChange={(e) => setTarget(e.target.value)} />
                  <div className="cc-pane-actions">
                    <button className="cc-pane-btn primary" disabled={!target.trim()} onClick={doConference}>Add</button>
                    <button className="cc-pane-btn ghost" onClick={closePane}>Cancel</button>
                  </div>
                </div>
              )}
              <div className="cc-notes">
                <textarea value={call.notes} placeholder="Call notes (saved to the CRM activity)…"
                  onChange={(e) => setNotes(e.target.value)} />
              </div>
            </>
          )}
          <button className="cc-hangup" onClick={hangup} aria-label="Hang up" disabled={status === "ended"}>
            <IconHangup />
          </button>
        </>
      )}
    </div>
  );
}
